import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { AlertTriangle, ChevronLeft, ChevronRight, Check, Lock, ChevronsUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../ui/command';
import SubcontractOfferItemsSelector from './SubcontractOfferItemsSelector';
import SubcontractOfferPricingStep from './SubcontractOfferPricingStep';
import { createOffer, updateOffer, getOffer, extractApiError, getAvailability } from '../../lib/subcontractOfferApi';

const API = process.env.REACT_APP_BACKEND_URL;
const STEP_LABELS = ['Subappaltatore', 'Lavorazioni', 'Economia', 'Riepilogo'];

/**
 * Wizard 4-step per creare o MODIFICARE una bozza Offerta di Subappalto.
 *
 * Props:
 * - open, onOpenChange
 * - project: { id, project_number, site_address, property_address, ... }
 * - isAdmin: bool (usato per mostrare valori interni)
 * - editingOffer: null (create mode) | offer object (edit mode; deve essere in stato 'bozza')
 * - onCreated: (offer) => void — callback creazione
 * - onUpdated: (offer) => void — callback aggiornamento (edit mode)
 * - parentSubcontractId: string | null (F.6.2A) — se valorizzato, l'Offerta viene
 *   creata come "Offerta extra" del contratto padre indicato. Passato al backend
 *   nel payload createOffer.
 * - presetSupplierId: string | null (F.6.2A) — se valorizzato, precompila e blocca
 *   il subappaltatore (usato con parentSubcontractId per far quadrare i vincoli backend).
 */
export const SubcontractOfferWizard = ({
  open, onOpenChange, project, isAdmin, editingOffer, onCreated, onUpdated,
  parentSubcontractId = null, presetSupplierId = null,
}) => {
  const isEdit = !!editingOffer;
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [conflict, setConflict] = useState(false);
  const [confirmExitOpen, setConfirmExitOpen] = useState(false);
  const [initialSnapshot, setInitialSnapshot] = useState(null);

  // Data
  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemQuantities, setItemQuantities] = useState({});
  const [pricing, setPricing] = useState({
    pricing_mode: null,
    agreed_total_amount: null,
    discount_percent: null,
    valid_until: '',
    notes: '',
    exclusions: '',
    conditions: '',
  });
  const [rowRevision, setRowRevision] = useState(0);

  // Availability preloaded per il riepilogo (nome/descrizione voci selezionate)
  const [availability, setAvailability] = useState(null);

  useEffect(() => {
    if (!open) return;
    // Reset ad ogni apertura
    setStep(1);
    setSubmitting(false);
    setError(null);
    setConflict(false);
    setConfirmExitOpen(false);
    setAvailability(null);
    // Load suppliers (SOLO se in create mode; edit mode li usa in read-only via editingOffer)
    if (!isEdit) {
      axios.get(`${API}/api/suppliers?limit=500`, { withCredentials: true })
        .then(r => setSuppliers((r.data.items || []).filter(s => !s.soft_deleted)))
        .catch(() => setSuppliers([]));
    } else {
      // In edit mode: reperisco solo il supplier corrente per il display
      axios.get(`${API}/api/suppliers/${editingOffer.supplier_id}`, { withCredentials: true })
        .then(r => setSuppliers([r.data]))
        .catch(() => setSuppliers([{
          id: editingOffer.supplier_id,
          ragione_sociale: editingOffer.supplier_snapshot?.ragione_sociale || '—',
          partita_iva: editingOffer.supplier_snapshot?.partita_iva || '—',
          category: editingOffer.supplier_snapshot?.category || '—',
        }]));
    }
    // Preload availability per riepilogo (viene rifetchato dallo Step 2)
    getAvailability(project.id).then(setAvailability).catch(() => {});

    if (isEdit) {
      // Precarica campi dalla bozza (spec F.3.1 §2 + F.3.2 §2)
      setSupplierId(editingOffer.supplier_id);
      const items = (editingOffer.items || []).map(i => i.customer_contract_item_id);
      setSelectedItems(items);
      setItemQuantities(Object.fromEntries(
        (editingOffer.items || []).map(i => [i.customer_contract_item_id, i.quantity != null ? Number(i.quantity) : undefined])
      ));
      const preloadedPricing = {
        pricing_mode: editingOffer.pricing_mode || null,
        agreed_total_amount: editingOffer.pricing_mode === 'agreed_total' ? editingOffer.agreed_total_amount : null,
        discount_percent: editingOffer.pricing_mode === 'discount_percent' ? editingOffer.discount_percent : null,
        valid_until: editingOffer.valid_until || '',
        notes: editingOffer.notes || '',
        exclusions: editingOffer.exclusions || '',
        conditions: editingOffer.conditions || '',
      };
      setPricing(preloadedPricing);
      setRowRevision(editingOffer.row_revision);
      // Snapshot iniziale per dirty tracking
      setInitialSnapshot({
        supplierId: editingOffer.supplier_id,
        selectedItems: [...items].sort(),
        pricing: { ...preloadedPricing },
      });
    } else {
      setSupplierId(presetSupplierId || null);
      setSelectedItems([]);
      setItemQuantities({});
      const emptyPricing = { pricing_mode: null, agreed_total_amount: null, discount_percent: null, valid_until: '', notes: '', exclusions: '', conditions: '' };
      setPricing(emptyPricing);
      setRowRevision(0);
      setInitialSnapshot({ supplierId: presetSupplierId || null, selectedItems: [], pricing: { ...emptyPricing } });
    }
  }, [open, project.id, isEdit, editingOffer, presetSupplierId]);

  // ==============================
  // Dirty tracking (unsaved changes)
  // ==============================
  const isDirty = useMemo(() => {
    if (!initialSnapshot) return false;
    if (initialSnapshot.supplierId !== supplierId) return true;
    const s1 = [...selectedItems].sort();
    const s2 = initialSnapshot.selectedItems;
    if (s1.length !== s2.length) return true;
    for (let i = 0; i < s1.length; i++) if (s1[i] !== s2[i]) return true;
    const p1 = pricing, p2 = initialSnapshot.pricing;
    return (
      String(p1.pricing_mode || '') !== String(p2.pricing_mode || '') ||
      String(p1.agreed_total_amount || '') !== String(p2.agreed_total_amount || '') ||
      String(p1.discount_percent || '') !== String(p2.discount_percent || '') ||
      String(p1.valid_until || '') !== String(p2.valid_until || '') ||
      String(p1.notes || '') !== String(p2.notes || '') ||
      String(p1.exclusions || '') !== String(p2.exclusions || '') ||
      String(p1.conditions || '') !== String(p2.conditions || '')
    );
  }, [initialSnapshot, supplierId, selectedItems, pricing]);

  // Intercetto la chiusura per warning unsaved changes
  const requestClose = () => {
    if (submitting) return;
    if (isDirty && !conflict) {
      setConfirmExitOpen(true);
    } else {
      onOpenChange(false);
    }
  };

  const selectedItemDetails = useMemo(() => {
    if (!availability) return [];
    const bySelected = new Set(selectedItems);
    return (availability.items || []).filter(i => bySelected.has(i.customer_contract_item_id));
  }, [availability, selectedItems]);

  const baseInternalAmount = useMemo(() => {
    return selectedItemDetails.reduce((s, i) => {
      if (i.is_residual) {
        const q = itemQuantities[i.customer_contract_item_id];
        const qty = (q === '' || q == null) ? Number(i.available_quantity) || 0 : Number(q) || 0;
        return s + qty * (Number(i.internal_unit_price) || 0);
      }
      return s + (Number(i.internal_line_total) || 0);
    }, 0);
  }, [selectedItemDetails, itemQuantities]);

  const selectedSupplier = useMemo(() => suppliers.find(s => s.id === supplierId) || null, [suppliers, supplierId]);

  // =====================
  // Validazioni per step
  // =====================
  const canGoNext = useMemo(() => {
    if (step === 1) return !!supplierId;
    if (step === 2) return selectedItems.length > 0;
    if (step === 3) {
      // valid_until obbligatorio solo in emissione, ma resta necessario che l'operatore
      // possa proseguire a riepilogo anche senza pricing → richiediamo almeno pricing_mode
      // se ha già scelto una modalità: che ci sia il valore.
      if (!pricing.pricing_mode) return true; // può proseguire senza pricing (bozza)
      if (pricing.pricing_mode === 'agreed_total') {
        const v = Number(pricing.agreed_total_amount);
        return !!pricing.agreed_total_amount && !isNaN(v) && v >= 0;
      }
      if (pricing.pricing_mode === 'discount_percent') {
        const v = Number(pricing.discount_percent);
        return !!pricing.discount_percent && !isNaN(v) && v >= 0 && v < 100;
      }
      return false;
    }
    return true;
  }, [step, supplierId, selectedItems, pricing]);

  const submit = async () => {
    if (submitting) return;
    if (!supplierId || selectedItems.length === 0) {
      toast.error('Selezione incompleta');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit) {
        // ==== EDIT MODE ====
        const payload = {
          row_revision: rowRevision,
          items: selectedItems.map(id => ({ customer_contract_item_id: id, ...(itemQuantities[id] != null && !Number.isNaN(Number(itemQuantities[id])) ? { quantity: Number(itemQuantities[id]) } : {}) })),
        };
        // Pricing: mando sempre pricing_mode + valore coerente (o reset_pricing se azzerato)
        if (pricing.pricing_mode === 'agreed_total') {
          payload.pricing_mode = 'agreed_total';
          payload.agreed_total_amount = pricing.agreed_total_amount ? String(pricing.agreed_total_amount) : null;
          payload.discount_percent = null;
        } else if (pricing.pricing_mode === 'discount_percent') {
          payload.pricing_mode = 'discount_percent';
          payload.discount_percent = pricing.discount_percent ? String(pricing.discount_percent) : null;
          payload.agreed_total_amount = null;
        } else {
          // pricing_mode è stato azzerato → reset
          payload.reset_pricing = true;
        }
        if (pricing.valid_until !== undefined) payload.valid_until = pricing.valid_until || null;
        // F.3.2 — dati commerciali: uso update_documental_fields per aggiornarli come blocco
        payload.update_documental_fields = true;
        payload.notes = pricing.notes || null;
        payload.exclusions = pricing.exclusions || null;
        payload.conditions = pricing.conditions || null;

        const updated = await updateOffer(editingOffer.id, payload);
        toast.success(`Bozza ${updated.offer_number} aggiornata`);
        onOpenChange(false);
        if (onUpdated) onUpdated(updated);
      } else {
        // ==== CREATE MODE ====
        const payload = {
          project_id: project.id,
          supplier_id: supplierId,
          items: selectedItems.map(id => ({ customer_contract_item_id: id, ...(itemQuantities[id] != null && !Number.isNaN(Number(itemQuantities[id])) ? { quantity: Number(itemQuantities[id]) } : {}) })),
        };
        // F.6.2A · Offerta EXTRA su un contratto padre esistente.
        if (parentSubcontractId) payload.parent_subcontract_id = parentSubcontractId;
        if (pricing.pricing_mode) payload.pricing_mode = pricing.pricing_mode;
        if (pricing.pricing_mode === 'agreed_total' && pricing.agreed_total_amount) {
          payload.agreed_total_amount = String(pricing.agreed_total_amount);
        }
        if (pricing.pricing_mode === 'discount_percent' && pricing.discount_percent) {
          payload.discount_percent = String(pricing.discount_percent);
        }
        if (pricing.valid_until) payload.valid_until = pricing.valid_until;
        // F.3.2 — dati commerciali: aggiungi solo se non vuoti
        if (pricing.notes) payload.notes = pricing.notes;
        if (pricing.exclusions) payload.exclusions = pricing.exclusions;
        if (pricing.conditions) payload.conditions = pricing.conditions;

        const offer = await createOffer(payload);
        toast.success(`Offerta ${offer.offer_number} creata in bozza`);
        onOpenChange(false);
        if (onCreated) onCreated(offer);
      }
    } catch (err) {
      if (err?.response?.status === 409) {
        // Conflict: mostra banner dedicato, non chiude il wizard
        setConflict(true);
        toast.error('I dati sono stati modificati da un altro utente. Ricarica la bozza prima di continuare.');
      } else {
        const msg = extractApiError(err, isEdit ? 'Errore aggiornamento bozza' : 'Errore creazione offerta');
        setError(msg);
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const reloadFromServer = async () => {
    if (!isEdit) return;
    try {
      const fresh = await getOffer(editingOffer.id);
      const items = (fresh.items || []).map(i => i.customer_contract_item_id);
      const nextPricing = {
        pricing_mode: fresh.pricing_mode || null,
        agreed_total_amount: fresh.pricing_mode === 'agreed_total' ? fresh.agreed_total_amount : null,
        discount_percent: fresh.pricing_mode === 'discount_percent' ? fresh.discount_percent : null,
        valid_until: fresh.valid_until || '',
        notes: fresh.notes || '',
        exclusions: fresh.exclusions || '',
        conditions: fresh.conditions || '',
      };
      setSelectedItems(items);
      setPricing(nextPricing);
      setRowRevision(fresh.row_revision);
      setInitialSnapshot({
        supplierId: fresh.supplier_id,
        selectedItems: [...items].sort(),
        pricing: { ...nextPricing },
      });
      setConflict(false);
      toast.success('Bozza ricaricata');
    } catch (err) {
      toast.error(extractApiError(err, 'Errore ricaricamento'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) requestClose(); else onOpenChange(true); }}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid={isEdit ? 'subcontract-offer-edit-wizard' : 'offer-wizard-dialog'}
        onEscapeKeyDown={(e) => { e.preventDefault(); requestClose(); }}
        onPointerDownOutside={(e) => { e.preventDefault(); requestClose(); }}
      >
        <DialogHeader>
          <DialogTitle data-testid="offer-wizard-title">
            {isEdit ? 'MODIFICA BOZZA OFFERTA' : 'Nuova Offerta di Subappalto'} — Step {step} di 4: {STEP_LABELS[step - 1]}
            {isEdit && (
              <span className="ml-2 text-sm text-slate-500 font-normal">
                ({editingOffer.offer_number} · Rev.{editingOffer.offer_revision_number})
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {conflict && (
          <Alert variant="destructive" data-testid="subcontract-offer-edit-conflict">
            <AlertTriangle className="w-4 h-4" />
            <AlertDescription className="flex items-center justify-between gap-2">
              <span>I dati sono stati modificati da un altro utente. Ricarica la bozza prima di continuare.</span>
              <span className="flex gap-2">
                <Button size="sm" variant="outline" onClick={reloadFromServer} data-testid="subcontract-offer-edit-reload">
                  Ricarica bozza
                </Button>
                <Button size="sm" variant="ghost" onClick={() => onOpenChange(false)} data-testid="subcontract-offer-edit-cancel-conflict">
                  Annulla modifica
                </Button>
              </span>
            </AlertDescription>
          </Alert>
        )}

        {/* Progress */}
        <div className="flex gap-1 mb-4">
          {STEP_LABELS.map((_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded ${i + 1 <= step ? 'bg-[#FF7819]' : 'bg-slate-200'}`}
              data-testid={`offer-wizard-step-indicator-${i + 1}`}
            />
          ))}
        </div>

        {/* Body */}
        <div className="min-h-[300px]">
          {step === 1 && (
            (isEdit || !!presetSupplierId) ? (
              <LockedSupplierStep supplier={selectedSupplier} />
            ) : (
              <SupplierPickerStep suppliers={suppliers} value={supplierId} onChange={setSupplierId} />
            )
          )}
          {step === 2 && (
            <SubcontractOfferItemsSelector
              projectId={project.id}
              value={selectedItems}
              quantities={itemQuantities}
              onQuantityChange={(cid, val) => setItemQuantities(q => ({ ...q, [cid]: val }))}
              onChange={(next) => {
                setSelectedItems(next);
                getAvailability(project.id).then(setAvailability).catch(() => {});
              }}
              showInternalBase={!!isAdmin}
              excludeOfferId={isEdit ? editingOffer.id : null}
              editingOfferItemIds={isEdit ? (editingOffer.items || []).map(i => i.customer_contract_item_id) : []}
              testIdPrefix={isEdit ? 'subcontract-offer-edit-items' : 'items-selector'}
            />
          )}
          {step === 3 && (
            <SubcontractOfferPricingStep
              baseInternalAmount={baseInternalAmount}
              showInternalPreview={!!isAdmin}
              pricingMode={pricing.pricing_mode}
              agreedTotalAmount={pricing.agreed_total_amount}
              discountPercent={pricing.discount_percent}
              validUntil={pricing.valid_until}
              notes={pricing.notes}
              exclusions={pricing.exclusions}
              conditions={pricing.conditions}
              onChange={(patch) => setPricing(prev => ({ ...prev, ...patch }))}
              testIdPrefix={isEdit ? 'subcontract-offer-edit' : 'pricing'}
            />
          )}
          {step === 4 && (
            <RecapStep
              project={project}
              supplier={selectedSupplier}
              items={selectedItemDetails}
              pricing={pricing}
              baseInternalAmount={baseInternalAmount}
              isAdmin={!!isAdmin}
              quantities={itemQuantities}
              isEdit={isEdit}
            />
          )}
        </div>

        {error && (
          <Alert variant="destructive" data-testid="offer-wizard-error">
            <AlertTriangle className="w-4 h-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <div className="flex justify-between w-full">
            <div>
              {step > 1 && (
                <Button variant="outline" onClick={() => setStep(s => s - 1)} disabled={submitting} data-testid="offer-wizard-back-btn">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Indietro
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={requestClose}
                disabled={submitting}
                data-testid={isEdit ? 'subcontract-offer-edit-cancel' : 'offer-wizard-cancel-btn'}
              >
                Annulla
              </Button>
              {step < 4 && (
                <Button
                  onClick={() => setStep(s => s + 1)}
                  disabled={!canGoNext}
                  className="bg-[#FF7819] hover:bg-[#e56811] text-white"
                  data-testid="offer-wizard-next-btn"
                >
                  Avanti <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
              {step === 4 && (
                <Button
                  onClick={submit}
                  disabled={submitting || selectedItems.length === 0 || !supplierId || conflict || (isEdit && !isDirty)}
                  className="bg-[#FF7819] hover:bg-[#e56811] text-white"
                  data-testid={isEdit ? 'subcontract-offer-edit-save' : 'offer-wizard-submit-btn'}
                >
                  <Check className="w-4 h-4 mr-1" />
                  {submitting
                    ? (isEdit ? 'Salvataggio...' : 'Creazione...')
                    : (isEdit ? 'Salva modifiche' : 'Crea bozza offerta')}
                </Button>
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>

      {/* Alert dialog uscita con modifiche non salvate */}
      <AlertDialog open={confirmExitOpen} onOpenChange={setConfirmExitOpen}>
        <AlertDialogContent data-testid="subcontract-offer-edit-unsaved-warning">
          <AlertDialogHeader>
            <AlertDialogTitle>Hai modifiche non salvate</AlertDialogTitle>
            <AlertDialogDescription>
              Vuoi uscire senza salvarle? Le modifiche verranno perse.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="subcontract-offer-edit-unsaved-stay">Torna al wizard</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setConfirmExitOpen(false); onOpenChange(false); }}
              data-testid="subcontract-offer-edit-unsaved-leave"
            >
              Esci senza salvare
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};

// Sub-component: subappaltatore in sola lettura (edit mode)
const LockedSupplierStep = ({ supplier }) => (
  <div className="space-y-3" data-testid="subcontract-offer-edit-supplier-locked">
    <Alert className="bg-slate-50 border-slate-300">
      <Lock className="w-4 h-4" />
      <AlertDescription>
        Il subappaltatore non è modificabile su una bozza esistente. Per cambiare fornitore, annulla questa bozza e creane una nuova.
      </AlertDescription>
    </Alert>
    {supplier && (
      <div className="p-4 border border-slate-200 rounded-lg bg-white">
        <div className="font-semibold text-lg">{supplier.ragione_sociale}</div>
        <div className="text-sm text-slate-600 mt-1">
          P.IVA {supplier.partita_iva || '—'} · Categoria: {supplier.category || '—'}
        </div>
      </div>
    )}
  </div>
);

// =====================================================================
// SUB-COMPONENTS locali
// =====================================================================
const SupplierPickerStep = ({ suppliers, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selected = suppliers.find(s => s.id === value) || null;
  const filtered = suppliers.filter(s => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (s.ragione_sociale || '').toLowerCase().includes(q) || (s.partita_iva || '').toLowerCase().includes(q);
  });

  return (
    <div className="space-y-3" data-testid="supplier-picker">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal h-auto py-2"
            data-testid="supplier-picker-trigger"
          >
            {selected ? (
              <span className="flex flex-col items-start text-left">
                <span className="font-medium">{selected.ragione_sociale}</span>
                <span className="text-xs text-slate-500">
                  P.IVA {selected.partita_iva || '—'}{selected.category ? ` · Categoria: ${selected.category}` : ''}
                </span>
              </span>
            ) : (
              <span className="text-slate-500">Seleziona subappaltatore…</span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Cerca per ragione sociale o P.IVA…"
              value={query}
              onValueChange={setQuery}
              data-testid="supplier-picker-search"
            />
            <CommandList>
              <CommandEmpty>Nessun fornitore trovato</CommandEmpty>
              <CommandGroup>
                {filtered.map(s => (
                  <CommandItem
                    key={s.id}
                    value={s.id}
                    onSelect={() => { onChange(s.id); setOpen(false); }}
                    data-testid={`supplier-picker-item-${s.id}`}
                    className="flex items-start gap-2"
                  >
                    <Check className={`mt-1 h-4 w-4 shrink-0 ${value === s.id ? 'opacity-100 text-orange-600' : 'opacity-0'}`} />
                    <span className="flex flex-col">
                      <span className="font-medium">{s.ragione_sociale}</span>
                      <span className="text-xs text-slate-500">
                        P.IVA {s.partita_iva || '—'} · Categoria: {s.category || '—'}
                        {s.vat_regime ? ` · Regime: ${s.vat_regime}` : ''}
                      </span>
                      {s.alerts && s.alerts.length > 0 && (
                        <span className="text-xs text-amber-700 mt-0.5 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> {s.alerts.length} alert anagrafici
                        </span>
                      )}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selected && (
        <div className="text-sm text-slate-600" data-testid="supplier-picker-selected">
          Selezionato: <span className="font-medium text-slate-800">{selected.ragione_sociale}</span>
          <span className="text-slate-500"> · P.IVA {selected.partita_iva || '—'}</span>
        </div>
      )}
    </div>
  );
};

const RecapStep = ({ project, supplier, items, pricing, baseInternalAmount, isAdmin, quantities = {} }) => {
  const previewAgreedTotal = useMemo(() => {
    if (!pricing.pricing_mode) return null;
    if (pricing.pricing_mode === 'agreed_total') return Number(pricing.agreed_total_amount) || 0;
    if (pricing.pricing_mode === 'discount_percent') {
      const dp = Number(pricing.discount_percent) || 0;
      return baseInternalAmount * (100 - dp) / 100;
    }
    return null;
  }, [pricing, baseInternalAmount]);

  return (
    <div className="space-y-4 text-sm" data-testid="offer-wizard-recap">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="text-slate-500 text-xs">Progetto</div>
          <div className="font-medium">{project?.project_number || project?.id}</div>
        </div>
        <div>
          <div className="text-slate-500 text-xs">Cantiere</div>
          <div className="font-medium">{project?.site_address || project?.property_address || '—'}</div>
        </div>
        <div>
          <div className="text-slate-500 text-xs">Subappaltatore</div>
          <div className="font-medium" data-testid="recap-supplier">{supplier?.ragione_sociale || '—'}</div>
          <div className="text-xs text-slate-500">P.IVA {supplier?.partita_iva || '—'}</div>
        </div>
        <div>
          <div className="text-slate-500 text-xs">Modalità economica</div>
          <div className="font-medium">
            {pricing.pricing_mode === 'agreed_total' && 'Prezzo pattuito complessivo'}
            {pricing.pricing_mode === 'discount_percent' && 'Ribasso percentuale'}
            {!pricing.pricing_mode && <span className="text-slate-400">— (da definire prima dell&apos;emissione)</span>}
          </div>
        </div>
        {previewAgreedTotal !== null && (
          <div>
            <div className="text-slate-500 text-xs">Importo finale (anteprima)</div>
            <div className="font-medium" data-testid="recap-total-preview">€ {previewAgreedTotal.toFixed(2)}</div>
          </div>
        )}
        {pricing.valid_until && (
          <div>
            <div className="text-slate-500 text-xs">Validità</div>
            <div className="font-medium">{pricing.valid_until}</div>
          </div>
        )}
      </div>

      <div>
        <div className="text-slate-500 text-xs mb-2">Lavorazioni selezionate ({items.length})</div>
        <table className="w-full text-sm border border-slate-200 rounded overflow-hidden">
          <thead className="bg-slate-50">
            <tr>
              <th className="p-2 text-left">Cat.</th>
              <th className="p-2 text-left">Codice</th>
              <th className="p-2 text-left">Descrizione</th>
              <th className="p-2 text-left">U.M.</th>
              <th className="p-2 text-right">Qtà</th>
            </tr>
          </thead>
          <tbody>
            {items.map(it => (
              <tr key={it.customer_contract_item_id} className="border-t border-slate-100">
                <td className="p-2">{it.category}</td>
                <td className="p-2 font-mono text-xs">{it.code}</td>
                <td className="p-2">{it.description}</td>
                <td className="p-2">{it.unit}</td>
                <td className="p-2 text-right">
                  {(() => {
                    if (!it.is_residual) return it.quantity;
                    const q = quantities[it.customer_contract_item_id];
                    const offered = (q === '' || q == null) ? it.available_quantity : q;
                    return offered;
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isAdmin && (
        <div className="text-xs text-slate-500 border-t pt-2">
          Valore base interno (uso amministrativo): € {baseInternalAmount.toFixed(2)}
        </div>
      )}

      {pricing.notes && (
        <div>
          <div className="text-slate-500 text-xs">Note</div>
          <div className="text-sm whitespace-pre-wrap" data-testid="recap-notes">{pricing.notes}</div>
        </div>
      )}
      {pricing.exclusions && (
        <div>
          <div className="text-slate-500 text-xs">Esclusioni</div>
          <div className="text-sm whitespace-pre-wrap" data-testid="recap-exclusions">{pricing.exclusions}</div>
        </div>
      )}
      {pricing.conditions && (
        <div>
          <div className="text-slate-500 text-xs">Condizioni</div>
          <div className="text-sm whitespace-pre-wrap" data-testid="recap-conditions">{pricing.conditions}</div>
        </div>
      )}
    </div>
  );
};

export default SubcontractOfferWizard;
